import { useEffect, useState } from 'react';
import type { MasterList } from '@kkp-fraud/shared';
import { COOKIE_MODE, api, type ApiSettings } from '../api';
import { DEMO } from '../demo';

/**
 * The Master lists a rule may reference (R7 — `in_set` / `not_in_set`), for the rule editor's
 * list choice and its live check.
 *
 * `null` means NOT KNOWN, never "there are none": a build with no admin service (password mode
 * answers `[]` without asking anybody — the same reason Master Management shows its
 * "unavailable" banner), a load still in flight, or one that failed. The editor then offers
 * only the list a clause already names and leaves "does it exist" to the server, instead of
 * calling every list unknown and refusing Apply. With `VITE_DEMO=1` the demo lists are known.
 */
export function useMasterLists(settings: ApiSettings): readonly MasterList[] | null {
  const [lists, setLists] = useState<readonly MasterList[] | null>(null);
  const known = COOKIE_MODE || DEMO;
  // Keyed on the address, not the object: a caller that builds `settings` per render must not refetch per render.
  const { baseUrl } = settings;

  useEffect(() => {
    if (!known) return;
    let cancelled = false;
    api.masters.list({ baseUrl }).then(
      (got) => {
        if (!cancelled) setLists(Array.isArray(got) ? got : null);
      },
      () => {
        // A failed load is "not known": the server still decides on validate and save.
        if (!cancelled) setLists(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [known, baseUrl]);

  return known ? lists : null;
}
